<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { isWorkspaceV2 } = useWorkspaceV2();
const contactsFormRef = ref(null);
const saveError = ref('');
const refreshError = ref('');
const refreshing = ref(false);
let contextGeneration = 0;
let refreshRequest = 0;
const submitting = ref(false);
const avatarBusy = ref(false);
const discardRef = ref(null);
let resolveNavigation = null;
const finishNavigation = allowed => {
  const resolve = resolveNavigation;
  resolveNavigation = null;
  if (resolve) discardRef.value?.close();
  resolve?.(allowed);
};
const guardNavigation = () => {
  if (!isWorkspaceV2.value) return true;
  if (submitting.value || avatarBusy.value) return false;
  if (!contactsFormRef.value?.isDirty) return true;
  resolveNavigation?.(false);
  return new Promise(resolve => {
    resolveNavigation = resolve;
    discardRef.value?.open();
  });
};
onBeforeRouteLeave(guardNavigation);
onBeforeRouteUpdate(guardNavigation);
const beforeUnload = event => {
  if (!isWorkspaceV2.value || !contactsFormRef.value?.isDirty) return;
  event.preventDefault();
  event.returnValue = '';
};
onMounted(() => window.addEventListener('beforeunload', beforeUnload));
onBeforeUnmount(() => {
  contextGeneration += 1;
  refreshRequest += 1;
  window.removeEventListener('beforeunload', beforeUnload);
  resolveNavigation?.(false);
});

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});
const contactContext = computed(
  () => `${route.params.accountId || ''}:${props.selectedContact?.id || ''}`
);
watch(
  contactContext,
  () => {
    contextGeneration += 1;
    refreshRequest += 1;
    refreshError.value = '';
    refreshing.value = false;
  },
  { flush: 'sync' }
);

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const refreshContactableInbox = async ({ allowDuringSubmit = false } = {}) => {
  if (
    refreshing.value ||
    (!allowDuringSubmit && submitting.value) ||
    avatarBusy.value
  )
    return;
  refreshRequest += 1;
  const request = refreshRequest;
  const generation = contextGeneration;
  refreshing.value = true;
  refreshError.value = '';
  try {
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
  } catch {
    if (request !== refreshRequest || generation !== contextGeneration) return;
    refreshError.value = t(
      'CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.REFRESH_ERROR_MESSAGE'
    );
    useAlert(refreshError.value);
  } finally {
    if (request === refreshRequest && generation === contextGeneration) {
      refreshing.value = false;
    }
  }
};
const retryContactableInbox = () => refreshContactableInbox();

const updateContact = async () => {
  if (
    submitting.value ||
    avatarBusy.value ||
    isUpdating.value ||
    isFormInvalid.value
  )
    return;
  submitting.value = true;
  saveError.value = '';
  refreshError.value = '';
  const generation = contextGeneration;
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    await store.dispatch('contacts/update', basicContactData);
    if (generation !== contextGeneration) return;
    contactsFormRef.value?.markSaved();
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
    await refreshContactableInbox({ allowDuringSubmit: true });
  } catch (error) {
    if (generation !== contextGeneration) return;
    saveError.value = t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE');
    useAlert(saveError.value);
  } finally {
    submitting.value = false;
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  if (avatarBusy.value || submitting.value) return;
  avatarBusy.value = true;
  const previousUrl = avatarUrl.value;
  const previousFile = avatarFile.value;
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      id: props.selectedContact.id,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    avatarUrl.value = previousUrl;
    avatarFile.value = previousFile;
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  } finally {
    avatarBusy.value = false;
  }
};

const handleAvatarDelete = async () => {
  if (avatarBusy.value || submitting.value) return;
  avatarBusy.value = true;
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  } finally {
    avatarBusy.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        :allow-upload="!avatarBusy && !submitting"
        :aria-busy="avatarBusy"
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date:
                  lastActivityAt || $t('CONTACTS_LAYOUT.DETAILS.NO_ACTIVITY'),
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
    </div>
    <div class="flex w-full flex-col items-start gap-6">
      <fieldset class="m-0 w-full min-w-0 border-0 p-0" :disabled="submitting">
        <ContactsForm
          ref="contactsFormRef"
          :contact-data="contactData"
          is-details-view
          @update="handleFormUpdate"
        />
      </fieldset>
      <p
        v-if="isWorkspaceV2 && saveError"
        role="alert"
        class="text-sm text-n-ruby-11"
      >
        {{ saveError }}
      </p>
      <div
        v-if="isWorkspaceV2 && refreshError"
        class="flex items-center gap-3"
        role="alert"
      >
        <p class="m-0 min-w-0 flex-1 text-sm text-n-ruby-11">
          {{ refreshError }}
        </p>
        <Button
          variant="faded"
          color="slate"
          :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.RETRY_REFRESH')"
          class="shrink-0"
          :disabled="refreshing || submitting || avatarBusy"
          @click="retryContactableInbox"
        />
      </div>
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating || avatarBusy"
        :disabled="submitting || avatarBusy || isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
    <Policy :permissions="['administrator']">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
    <Dialog
      ref="discardRef"
      type="alert"
      :title="t('WORKSPACE_V2.CONTACT_EDITOR.DISCARD_TITLE')"
      :description="t('WORKSPACE_V2.CONTACT_EDITOR.DISCARD_DESCRIPTION')"
      :confirm-button-label="t('WORKSPACE_V2.TASK_EDITOR.DISCARD')"
      :cancel-button-label="t('WORKSPACE_V2.TASK_EDITOR.KEEP_EDITING')"
      @confirm="finishNavigation(true)"
      @close="finishNavigation(false)"
    />
  </div>
</template>
